import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { api, fmtMoney, fmtWhen } from "../../lib/peApi";
import { usePEAuth } from "./PEAuthContext";
import { PEFormRenderer, missingRequired } from "./PEForms";

const inputCls =
  "w-full rounded-sm border border-pe-sand/70 bg-white px-4 py-3 text-sm text-pe-forest outline-none focus:border-pe-gold transition-colors placeholder:text-pe-muted/60";
const CHAPTER_ORDER = ["signature", "prismhouse", "facials", "maintenance", "studio", "education"];
const CHAPTER_LABEL = { signature: "The Signature", prismhouse: "The Prism House — Hair", facials: "The Essence Facials — Skin", maintenance: "Prism Maintenance", studio: "The Prism Studio — Makeup", education: "Beauty Education" };

const chip = (on) =>
  `px-3.5 py-2 rounded-full border text-[12px] transition-colors ${on ? "border-pe-forest bg-pe-forest text-pe-ivory" : "border-pe-sand/70 bg-white text-pe-body hover:border-pe-gold"}`;

export default function BookingFlow({ initialService, onClose }) {
  const { user } = usePEAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [prefOptions, setPrefOptions] = useState(null);
  const [policies, setPolicies] = useState(null);
  const [serviceId, setServiceId] = useState(initialService || "");
  const [selections, setSelections] = useState({});
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [days, setDays] = useState({});
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [formDefs, setFormDefs] = useState(null);
  const [fullFormDefs, setFullFormDefs] = useState(null);
  const [editingForms, setEditingForms] = useState(false);
  const [formStatus, setFormStatus] = useState(null);
  const [formAnswers, setFormAnswers] = useState({ consultation: {}, intake: {} });
  const [prefs, setPrefs] = useState({ music_genres: [], music_other: "", music_volume: "no-preference", music_artists: "", entertainment: [], entertainment_other: "", conversation: "no-preference", comfort_note: "" });
  const [guest, setGuest] = useState({ name: "", email: "", phone: "" });
  const [safetyNote, setSafetyNote] = useState("");
  const [promo, setPromo] = useState("");
  const [ackPrivacy, setAckPrivacy] = useState(false);
  const [ackPolicy, setAckPolicy] = useState(false);
  const [step, setStep] = useState(0);
  const [quote, setQuote] = useState(null);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const service = useMemo(() => services.find((s) => s.id === serviceId), [services, serviceId]);
  const hasOptions = !!(service && service.option_groups && service.option_groups.length);

  useEffect(() => {
    api("/services").then((d) => setServices(d.services)).catch(() => {});
    api("/preferences/options").then(setPrefOptions).catch(() => {});
    api("/policies").then(setPolicies).catch(() => {});
  }, []);

  useEffect(() => {
    if (!serviceId) return;
    api(`/availability?service_id=${serviceId}&month=${month}`).then((d) => setDays(d.days)).catch(() => setDays({}));
    api(`/forms/definitions?service_id=${serviceId}`).then((d) => setFormDefs(d.forms)).catch(() => {});
  }, [serviceId, month]);

  useEffect(() => {
    if (user) {
      setPrefs((p) => ({ ...p, ...Object.fromEntries(Object.entries(user.prefs || {}).filter(([, v]) => v !== undefined && v !== "")) }));
      api("/me/forms").then((d) => setFormStatus(d.status)).catch(() => {});
      api("/forms/definitions").then((d) => setFullFormDefs(d.forms)).catch(() => {});
      // Prefill with the answers already on file so nothing is ever lost or re-typed.
      for (const key of ["consultation", "intake"]) {
        api(`/me/forms/${key}`).then((d) => {
          if (d.form?.answers) setFormAnswers((f) => ({ ...f, [key]: { ...(f[key] || {}), ...d.form.answers } }));
        }).catch(() => {});
      }
    }
  }, [user]);

  const shiftMonth = (dir) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(Date.UTC(y, m - 1 + dir, 1));
    setMonth(d.toISOString().slice(0, 7));
    setDate("");
    setSlot("");
  };

  const steps = [
    "Service",
    ...(hasOptions ? ["Options"] : []),
    "Time",
    "Forms",
    "Preferences",
    ...(user ? [] : ["Contact"]),
    "Review",
    "Deposit",
  ];
  const stepName = steps[step];

  const formsDone = user
    ? formStatus && formStatus.consultation.complete && formStatus.intake.complete
    : false; // guests always see the forms step (service-relevant questions)

  const next = async () => {
    setError("");
    if (stepName === "Service" && !serviceId) return setError("Please choose a service.");
    if (stepName === "Options") {
      const missingGroup = service.option_groups.find((g) => g.required && !selections[g.key]);
      if (missingGroup) return setError(`Please choose: ${missingGroup.label}.`);
    }
    if (stepName === "Time" && !slot) return setError("Please choose a date and time.");
    if (stepName === "Forms") {
      // One Continue validates and saves BOTH forms — answers are never lost,
      // and the server-side gate can't bounce you back here.
      const defs = user ? fullFormDefs : formDefs;
      if (!defs) return setError("Your forms are still loading — one moment.");
      for (const def of defs) {
        const missing = missingRequired(def, formAnswers[def.key] || {});
        if (missing.length) {
          return setError(`${def.title}: please complete — ${missing.map((q) => q.label).slice(0, 3).join("; ")}${missing.length > 3 ? "…" : ""}`);
        }
      }
      if (user) {
        setBusy(true);
        try {
          let status = null;
          for (const def of defs) {
            const r = await api(`/me/forms/${def.key}`, { method: "PUT", body: { answers: formAnswers[def.key] || {} } });
            status = r.status;
          }
          if (status) setFormStatus(status);
          setEditingForms(false);
        } catch (e) {
          setBusy(false);
          return setError(e.message);
        }
        setBusy(false);
      }
    }
    if (stepName === "Contact") {
      if (!guest.name.trim() || !guest.email.includes("@") || guest.phone.trim().length < 7)
        return setError("Please provide your name, email and mobile number — we need them to process your appointment and send your secure access code.");
    }
    if (stepName === "Review") {
      if (!ackPrivacy) return setError("Please read and acknowledge the privacy notice to continue.");
      if (!ackPolicy) return setError("Please read and acknowledge the cancellation and rescheduling policy to continue.");
      return confirmBooking();
    }
    if (stepName === "Preferences") {
      // Load the itemized quote for the Review step.
      setBusy(true);
      try {
        const d = await api("/quote", { method: "POST", body: { service_id: serviceId, selections, promo_code: promo, guest: user ? undefined : guest } });
        setQuote(d.quote);
      } catch (e) {
        setBusy(false);
        return setError(e.message);
      }
      setBusy(false);
    }
    setStep((s) => s + 1);
  };

  const confirmBooking = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await api("/bookings", {
        method: "POST",
        body: {
          service_id: serviceId,
          start: slot,
          selections,
          prefs,
          safety_note: safetyNote,
          promo_code: promo,
          privacy_ack: ackPrivacy,
          policy_ack: ackPolicy,
          origin_url: window.location.origin,
          ...(user ? {} : { guest, forms: { consultation: { answers: formAnswers.consultation }, intake: { answers: formAnswers.intake } } }),
        },
      });
      setBooking(result);
      setQuote(result.quote);
      setStep(steps.length - 1);
    } catch (e) {
      if ((e.message || "").startsWith("forms_required")) {
        setError("Your Consultation Form and Client Intake Form are required before booking — complete them below.");
        setStep(steps.indexOf("Forms"));
      } else {
        setError(e.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const pay = async () => {
    setBusy(true);
    setError("");
    try {
      const { checkout_url } = await api(`/bookings/${booking.booking_id}/pay`, { method: "POST", body: user ? {} : { guest_email: guest.email } });
      window.location.href = checkout_url;
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const summaryRow = (label, value, strong) => (
    <div className="flex justify-between gap-4" key={label}>
      <span className="text-pe-muted">{label}</span>
      <span className={strong ? "font-pe text-pe-brass text-lg" : "text-pe-body text-right"}>{value}</span>
    </div>
  );

  return (
    <div data-testid="booking-flow" role="dialog" aria-modal="true" aria-label="Book your Prismatic Essence service" className="fixed inset-0 z-[95] overflow-y-auto bg-pe-forest/40 backdrop-blur-sm flex items-start md:items-center justify-center p-4"
      onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
    >
      <div className="relative bg-pe-ivory w-full max-w-2xl rounded-sm border border-pe-gold/30 shadow-2xl my-8">
        <div className="flex items-center justify-between px-7 py-5 border-b border-pe-sand/50">
          <div className="flex items-center gap-3">
            <img src="/images/pe-logo.png" alt="" className="h-8 w-auto" />
            <div>
              <p className="font-pe text-lg text-pe-forest leading-none">Book your service</p>
              <p className="text-[10px] tracking-[0.2em] uppercase text-pe-muted mt-1">Step {step + 1} of {steps.length} — {stepName}</p>
            </div>
          </div>
          <button data-testid="booking-close" onClick={onClose} aria-label="Close booking" className="p-2 text-pe-muted hover:text-pe-forest"><X size={18} /></button>
        </div>

        <div className="p-7 md:p-9">
          {error && <p data-testid="booking-error" role="alert" className="mb-5 text-sm text-red-800 bg-red-50 border border-red-200 rounded-sm px-4 py-3">{error}</p>}

          {stepName === "Service" && (
            <div className="space-y-6 max-h-[55vh] overflow-y-auto pr-1">
              {CHAPTER_ORDER.map((ch) => (
                <div key={ch}>
                  <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-pe-olive mb-3">{CHAPTER_LABEL[ch]}</p>
                  <div className="space-y-2">
                    {services.filter((s) => s.chapter === ch && !s.contact_only).map((s) => (
                      <button
                        key={s.id}
                        data-testid={`booking-service-${s.id}`}
                        onClick={() => { setServiceId(s.id); setSlot(""); setDate(""); setSelections({}); }}
                        className={`w-full flex items-baseline justify-between gap-4 text-left rounded-sm border px-4 py-3 transition-colors ${serviceId === s.id ? "border-pe-gold bg-pe-gold/10" : "border-pe-sand/60 bg-white hover:border-pe-gold/60"}`}
                      >
                        <span className="font-pe text-lg text-pe-forest">{s.name}{s.starting && <span className="text-xs text-pe-muted"> (from)</span>}</span>
                        <span className="font-pe text-pe-brass shrink-0">{s.price ? `${s.starting ? "from " : ""}${fmtMoney(s.price)}` : "consult"}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {stepName === "Options" && service && (
            <div className="space-y-6" data-testid="booking-options">
              <p className="text-[12px] text-pe-muted leading-relaxed">Your total is calculated from these choices and shown before you pay — no surprises.</p>
              {service.option_groups.map((g) => (
                <div key={g.key}>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-2">{g.label} {g.required && <span className="text-pe-brass">*</span>}</p>
                  <div className="flex flex-wrap gap-2">
                    {g.choices.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        data-testid={`booking-option-${g.key}-${c.id}`}
                        onClick={() => setSelections((s) => ({ ...s, [g.key]: c.id }))}
                        className={chip(selections[g.key] === c.id)}
                      >
                        {c.label}{c.add ? ` · +$${c.add}` : ""}
                      </button>
                    ))}
                  </div>
                  {g.note && <p className="text-[11px] text-pe-muted mt-1.5">{g.note}</p>}
                </div>
              ))}
            </div>
          )}

          {stepName === "Time" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => shiftMonth(-1)} className="px-3 py-1 text-pe-olive hover:text-pe-forest">←</button>
                <p className="font-pe text-lg text-pe-forest">{new Date(month + "-02").toLocaleString(undefined, { month: "long", year: "numeric" })}</p>
                <button onClick={() => shiftMonth(1)} className="px-3 py-1 text-pe-olive hover:text-pe-forest">→</button>
              </div>
              <div className="grid grid-cols-7 gap-1 mb-5" data-testid="booking-calendar">
                {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <span key={i} className="text-center text-[10px] text-pe-muted py-1">{d}</span>)}
                {(() => {
                  const [y, m] = month.split("-").map(Number);
                  const first = new Date(Date.UTC(y, m - 1, 1));
                  const count = new Date(Date.UTC(y, m, 0)).getUTCDate();
                  const cells = [];
                  for (let i = 0; i < first.getUTCDay(); i++) cells.push(<span key={"e" + i} />);
                  for (let d = 1; d <= count; d++) {
                    const key = `${month}-${String(d).padStart(2, "0")}`;
                    const has = (days[key] || []).length > 0;
                    const dayLabel = new Date(key + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
                    cells.push(
                      <button
                        key={key}
                        data-testid={has ? `booking-day-${key}` : undefined}
                        aria-label={has ? `${dayLabel} — times available` : `${dayLabel} — unavailable`}
                        disabled={!has}
                        onClick={() => { setDate(key); setSlot(""); }}
                        className={`py-2 text-sm rounded-sm transition-colors ${date === key ? "bg-pe-forest text-pe-ivory" : has ? "bg-pe-gold/15 text-pe-forest hover:bg-pe-gold/30" : "text-pe-muted/40"}`}
                      >
                        {d}
                      </button>
                    );
                  }
                  return cells;
                })()}
              </div>
              {date && (
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-2">Available times — {service?.duration_label}</p>
                  <div className="flex flex-wrap gap-2" data-testid="booking-slots">
                    {(days[date] || []).map((iso) => (
                      <button
                        key={iso}
                        data-testid={`booking-slot-${iso.slice(11, 16)}`}
                        onClick={() => setSlot(iso)}
                        className={`px-4 py-2 rounded-full text-xs border transition-colors ${slot === iso ? "bg-pe-forest text-pe-ivory border-pe-forest" : "border-pe-sand text-pe-body hover:border-pe-gold"}`}
                      >
                        {new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {!Object.keys(days).length && <p className="text-sm text-pe-muted">No openings this month — try the next one.</p>}
            </div>
          )}

          {stepName === "Forms" && (
            <div className="space-y-6 max-h-[55vh] overflow-y-auto pr-1" data-testid="booking-forms">
              <div className="rounded-sm border border-pe-gold/40 bg-pe-cream/60 p-4">
                <p className="text-[12px] text-pe-body leading-relaxed">
                  <strong>Required before booking.</strong> Your Consultation Form and Client Intake Form keep your service safe and prepared.
                  {user ? " They live on your profile — complete them once and update anytime. Your answers save when you press Continue." : " As a guest you complete the questions relevant to your selected service right here — your answers stay on this page while you book."}
                  {" "}A flagged answer is never a diagnosis — it simply tells your provider to review before service.
                </p>
                {(() => {
                  const defs = user ? fullFormDefs : formDefs;
                  if (!defs) return null;
                  return (
                    <p className="text-[11px] mt-2" data-testid="booking-forms-progress">
                      {defs.map((def) => {
                        const missing = missingRequired(def, formAnswers[def.key] || {});
                        return (
                          <span key={def.key} className={`mr-4 ${missing.length ? "text-pe-muted" : "text-pe-olive"}`} data-testid={`booking-form-progress-${def.key}`}>
                            {def.title}: {missing.length ? `${missing.length} answer${missing.length > 1 ? "s" : ""} still needed` : "complete ✓"}
                          </span>
                        );
                      })}
                    </p>
                  );
                })()}
              </div>
              {user && formStatus && formStatus.consultation.complete && formStatus.intake.complete && !editingForms ? (
                <div className="rounded-sm border border-pe-sage/50 bg-white p-5" data-testid="booking-forms-complete">
                  <p className="font-pe text-lg text-pe-forest">Both forms are on file ✓</p>
                  <p className="text-[12px] text-pe-muted mt-1">
                    Consultation updated {new Date(formStatus.consultation.updated_at).toLocaleDateString()} · Intake updated {new Date(formStatus.intake.updated_at).toLocaleDateString()}.
                    Need to change something? Update them from your dashboard — or below.
                  </p>
                  <button data-testid="booking-forms-edit" onClick={() => setEditingForms(true)} className="mt-3 text-[11px] tracking-[0.2em] uppercase text-pe-brass underline underline-offset-4">Review my answers</button>
                </div>
              ) : !(user ? fullFormDefs : formDefs) ? (
                <p className="font-pe italic text-pe-olive">Loading your forms…</p>
              ) : (
                <div className="space-y-8">
                  {(user ? fullFormDefs : formDefs).map((def) => (
                    <div key={def.key} className="rounded-sm border border-pe-sand/60 bg-white p-6" data-testid={`booking-form-${def.key}`}>
                      <p className="font-pe text-xl text-pe-forest mb-4">{def.title}</p>
                      <PEFormRenderer
                        definition={def}
                        value={formAnswers[def.key] || {}}
                        onChange={(answers) => setFormAnswers((f) => ({ ...f, [def.key]: answers }))}
                        hideSubmit
                        testPrefix={`booking-form-${def.key}`}
                      />
                    </div>
                  ))}
                  <p className="text-[12px] text-pe-muted">Press Continue below — {user ? "both forms save to your profile at once" : "your answers are attached to your booking"}.</p>
                </div>
              )}
            </div>
          )}

          {stepName === "Preferences" && prefOptions && (
            <div className="space-y-6 max-h-[55vh] overflow-y-auto pr-1" data-testid="booking-preferences">
              <div>
                <p className="font-pe text-lg text-pe-forest mb-1">Music & Entertainment Preferences</p>
                <p className="text-[12px] text-pe-muted leading-relaxed">All optional. These shape a comfortable appointment — they're preferences, not promises; specific songs, artists or formats can't be guaranteed.</p>
              </div>
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-2">Music you enjoy (choose any)</p>
                <div className="flex flex-wrap gap-2">
                  {prefOptions.music_genres.map((g) => {
                    const on = prefs.music_genres.includes(g);
                    return <button key={g} type="button" data-testid={`booking-genre-${g.slice(0, 12).replace(/\W+/g, "-").toLowerCase()}`} onClick={() => setPrefs((p) => ({ ...p, music_genres: on ? p.music_genres.filter((x) => x !== g) : [...p.music_genres, g] }))} className={chip(on)}>{g}</button>;
                  })}
                </div>
                {prefs.music_genres.includes("Other — specify") && (
                  <input className={`${inputCls} mt-3`} placeholder="Tell us what else you enjoy…" value={prefs.music_other} onChange={(e) => setPrefs((p) => ({ ...p, music_other: e.target.value }))} />
                )}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <label className="block"><span className="block text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-1.5">Background volume</span>
                  <select data-testid="booking-volume" className={inputCls} value={prefs.music_volume} onChange={(e) => setPrefs((p) => ({ ...p, music_volume: e.target.value }))}>
                    {prefOptions.music_volume.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
                  </select>
                </label>
                <label className="block"><span className="block text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-1.5">Conversation</span>
                  <select data-testid="booking-conversation" className={inputCls} value={prefs.conversation} onChange={(e) => setPrefs((p) => ({ ...p, conversation: e.target.value }))}>
                    {prefOptions.conversation.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
                  </select>
                </label>
              </div>
              <label className="block"><span className="block text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-1.5">Artists or songs you love (optional)</span>
                <input data-testid="booking-artists" className={inputCls} maxLength={200} value={prefs.music_artists} onChange={(e) => setPrefs((p) => ({ ...p, music_artists: e.target.value }))} placeholder="A few favorites — requests can't be guaranteed" />
              </label>
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-2">Entertainment during your appointment (choose any)</p>
                <div className="flex flex-wrap gap-2">
                  {prefOptions.entertainment.map((g) => {
                    const on = prefs.entertainment.includes(g);
                    return <button key={g} type="button" data-testid={`booking-ent-${g.slice(0, 12).replace(/\W+/g, "-").toLowerCase()}`} onClick={() => setPrefs((p) => ({ ...p, entertainment: on ? p.entertainment.filter((x) => x !== g) : [...p.entertainment, g] }))} className={chip(on)}>{g}</button>;
                  })}
                </div>
                {prefs.entertainment.includes("Other — specify") && (
                  <input className={`${inputCls} mt-3`} placeholder="Something else you enjoy…" value={prefs.entertainment_other} onChange={(e) => setPrefs((p) => ({ ...p, entertainment_other: e.target.value }))} />
                )}
              </div>
              <label className="block"><span className="block text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-1.5">Comfort note (optional)</span>
                <textarea className={inputCls} rows={2} maxLength={500} value={prefs.comfort_note} onChange={(e) => setPrefs((p) => ({ ...p, comfort_note: e.target.value }))} placeholder="Anything that helps you relax…" />
                <span className="block text-[11px] text-pe-muted mt-1">Please don't include sensitive medical details here — your intake form covers safety privately.</span>
              </label>
              <label className="block"><span className="block text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-1.5">Promo code (optional)</span>
                <input data-testid="booking-promo" className={inputCls} value={promo} onChange={(e) => setPromo(e.target.value)} />
              </label>
            </div>
          )}

          {stepName === "Contact" && (
            <div className="space-y-5" data-testid="booking-contact">
              <div>
                <p className="font-pe text-lg text-pe-forest mb-1">Booking as a guest</p>
                <p className="text-[12px] text-pe-muted leading-relaxed">
                  No account needed — we only ask for the contact details required to process your appointment. After booking you'll receive a secure
                  one-time code by email to view or manage your appointment. Create an account or add a passkey later with the same email and your
                  bookings and forms attach to it automatically.
                </p>
              </div>
              <label className="block"><span className="block text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-1.5">Full name <span className="text-pe-brass">*</span></span>
                <input data-testid="booking-guest-name" className={inputCls} value={guest.name} onChange={(e) => setGuest((g) => ({ ...g, name: e.target.value }))} />
              </label>
              <label className="block"><span className="block text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-1.5">Email <span className="text-pe-brass">*</span></span>
                <input data-testid="booking-guest-email" type="email" className={inputCls} value={guest.email} onChange={(e) => setGuest((g) => ({ ...g, email: e.target.value }))} />
              </label>
              <label className="block"><span className="block text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-1.5">Mobile number <span className="text-pe-brass">*</span></span>
                <input data-testid="booking-guest-phone" type="tel" className={inputCls} placeholder="+1 …" value={guest.phone} onChange={(e) => setGuest((g) => ({ ...g, phone: e.target.value }))} />
              </label>
              <p className="text-[12px] text-pe-muted">
                Prefer an account? <button type="button" data-testid="booking-contact-signin" onClick={() => { sessionStorage.setItem("pe_return", `/prismatic-essence?book=${serviceId}`); navigate("/prismatic-essence/auth"); }} className="text-pe-brass underline underline-offset-4">Sign in or create one</button> — your place in this booking is kept.
              </p>
            </div>
          )}

          {stepName === "Review" && service && policies && (
            <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1" data-testid="booking-review">
              <div className="rounded-sm border border-pe-sand/60 bg-white p-6 space-y-2.5 text-sm">
                {summaryRow("Service", <span className="font-pe text-lg text-pe-forest">{service.name}</span>)}
                {summaryRow("When", fmtWhen(slot))}
                {summaryRow("Duration", service.duration_label)}
                {quote && summaryRow(service.starting ? "Starting price" : "Service price", `${service.starting ? "from " : ""}${fmtMoney(quote.service_price)}`)}
                {quote?.addons?.filter((a) => a.amount > 0).map((a) => summaryRow(`${a.group}: ${a.label}`, `+${fmtMoney(a.amount)}`))}
                {quote?.addons_total > 0 && summaryRow("Estimated total", fmtMoney(quote.subtotal))}
                {quote?.discount && summaryRow(quote.discount.label, `−${fmtMoney(quote.discount.amount)}`)}
                {summaryRow("Deposit due today", "$25.00", true)}
                {quote && summaryRow("Balance at appointment", quote.pending_confirmation ? "Pending price confirmation — approved by you before collection" : quote.balance !== null ? fmtMoney(quote.balance) : "Confirmed with you before collection")}
              </div>
              {quote?.pending_confirmation && (
                <p className="text-[12px] text-pe-muted leading-relaxed">Your selection needs the house to confirm the final price. The $25 deposit secures your time — no amount beyond it is collected without your approval.</p>
              )}
              <div className="rounded-sm border border-pe-gold/40 bg-pe-cream/60 p-5 space-y-3">
                <p className="font-pe text-lg text-pe-forest">Cancellation & rescheduling policy</p>
                <p className="text-[12px] text-pe-body leading-relaxed" data-testid="booking-policy-cancel">{policies.cancellation}</p>
                <p className="text-[12px] text-pe-body leading-relaxed" data-testid="booking-policy-reschedule">{policies.rescheduling}</p>
                <label className="flex items-start gap-3 text-[13px] text-pe-forest pt-1">
                  <input data-testid="booking-policy-ack" type="checkbox" checked={ackPolicy} onChange={(e) => setAckPolicy(e.target.checked)} className="mt-1 accent-[#C59B27]" />
                  <span>I have read and understand the cancellation and rescheduling policy. <span className="text-pe-brass">*</span></span>
                </label>
              </div>
              <div className="rounded-sm border border-pe-sand/60 bg-white p-5">
                <p className="font-pe text-lg text-pe-forest mb-2">Privacy notice</p>
                <p className="text-[12px] text-pe-body leading-relaxed mb-3">
                  Your information is collected only to schedule and prepare your Prismatic Essence appointment, communicate with you about your service, process your required deposit, manage your client account, administer loyalty and referral rewards, understand your comfort preferences, send appointment reminders, and provide a safe, personalized experience. It is never sold, rented, or shared for third-party marketing. A complete privacy policy is being finalized before launch.
                </p>
                <label className="flex items-start gap-3 text-[13px] text-pe-forest">
                  <input data-testid="booking-privacy-ack" type="checkbox" checked={ackPrivacy} onChange={(e) => setAckPrivacy(e.target.checked)} className="mt-1 accent-[#C59B27]" />
                  <span>I have read and understand the privacy notice. <span className="text-pe-brass">*</span></span>
                </label>
              </div>
            </div>
          )}

          {stepName === "Deposit" && quote && service && (
            <div className="space-y-4" data-testid="booking-summary">
              <div className="rounded-sm border border-pe-gold/40 bg-white p-6 space-y-2.5 text-sm">
                {summaryRow("Service", <span className="font-pe text-lg text-pe-forest">{service.name}</span>)}
                {summaryRow("When", fmtWhen(slot))}
                {!user && summaryRow("Booking as", `Guest — ${guest.email}`)}
                {quote.service_price ? summaryRow(service.starting ? "Starting price" : "Service price", `${service.starting ? "from " : ""}${fmtMoney(quote.service_price)}`) : summaryRow("Service price", "Quoted at consultation")}
                {quote.addons?.filter((a) => a.amount > 0).map((a) => summaryRow(`${a.group}: ${a.label}`, `+${fmtMoney(a.amount)}`))}
                {quote.addons_total > 0 && summaryRow("Estimated total", fmtMoney(quote.subtotal))}
                {quote.discount && summaryRow(quote.discount.label, `−${fmtMoney(quote.discount.amount)}`)}
                {summaryRow("Deposit due now", "$25.00", true)}
                {summaryRow("Balance at appointment", quote.pending_confirmation ? "Pending price confirmation" : quote.balance !== null ? fmtMoney(quote.balance) : "Confirmed with you before collection")}
              </div>
              <ul className="text-[12px] text-pe-muted space-y-1.5 list-disc pl-5">
                {quote.notes?.map((n) => <li key={n}>{n}</li>)}
                <li>Your time is held for 45 minutes. Your appointment is confirmed only after your deposit payment succeeds — unpaid requests never block the calendar.</li>
              </ul>
              <p className="text-[12px] text-pe-muted">Payment is handled securely by Stripe — we never see or store your card details.</p>
            </div>
          )}

          <div className="flex items-center justify-between mt-8 pt-5 border-t border-pe-sand/50">
            <button onClick={() => (step === 0 ? onClose() : setStep((s) => s - 1))} className="text-[11px] tracking-[0.2em] uppercase text-pe-muted hover:text-pe-forest">
              ← {step === 0 ? "Cancel" : "Back"}
            </button>
            {stepName === "Deposit" ? (
              <button data-testid="booking-pay" onClick={pay} disabled={busy} className="rounded-full bg-pe-brass text-pe-ivory px-8 py-3 font-sans text-[11px] tracking-[0.25em] uppercase hover:bg-pe-forest transition-colors disabled:opacity-50">
                {busy ? "Opening secure checkout…" : "Pay $25 deposit"}
              </button>
            ) : (
              <button data-testid={stepName === "Review" ? "booking-confirm" : "booking-next"} onClick={next} disabled={busy} className="rounded-full bg-pe-forest text-pe-ivory px-8 py-3 font-sans text-[11px] tracking-[0.25em] uppercase hover:bg-pe-olive transition-colors disabled:opacity-50">
                {busy ? "One moment…" : stepName === "Review" ? "Confirm booking" : stepName === "Forms" && formsDone ? "Continue" : "Continue"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

